import { useCallback, useEffect, useState } from 'react';
import { Download, LayoutDashboard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, EmptyBox, ErrorBox, LoadingBox, TabHeader, labelFor } from './AdminShared';

export const AdminStats = ({ authHeaders, tx }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [days, setDays] = useState(30);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${API}/api/admin/app-stats`, { headers: authHeaders() });
      if (!res.ok) throw new Error('stats');
      setStats(await res.json());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/admin/app-stats/export?days=${days}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('export');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `krug-stats-${days}d.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(tx.common.unavailable);
    } finally {
      setExporting(false);
    }
  };

  const groups = stats
    ? Object.entries(stats).filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v))
    : [];
  const scalars = stats ? Object.entries(stats).filter(([, v]) => !v || typeof v !== 'object') : [];

  return (
    <section data-testid="app-stats-section">
      <TabHeader icon={LayoutDashboard} title={tx.stats.title} onRefresh={load} refreshLabel={tx.common.refresh} testid="app-stats">
        <select
          data-testid="stats-export-days"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-full border border-white/15 bg-transparent px-3 py-2 text-xs font-bold text-slate-300 outline-none cursor-pointer"
        >
          {[7, 30, 90].map((d) => (
            <option key={d} value={d} className="bg-krug-navy">
              {d} {tx.stats.days}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-testid="stats-export-btn"
          onClick={exportCsv}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-full bg-krug-teal px-4 py-2 text-xs font-bold text-white hover:bg-krug-tealbright hover:text-krug-navy disabled:opacity-50 transition-colors duration-300"
        >
          {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          {tx.stats.exportCsv}
        </button>
      </TabHeader>

      <div className="mt-6">
        {loading && <LoadingBox text={tx.common.loading} testid="app-stats-loading" />}
        {!loading && error && (
          <ErrorBox text={tx.common.unavailable} retryText={tx.common.retry} onRetry={load} testid="app-stats-error" />
        )}
        {!loading && !error && stats && groups.length === 0 && scalars.length === 0 && (
          <EmptyBox text={tx.common.empty} testid="app-stats-empty" />
        )}
        {!loading && !error && stats && (groups.length > 0 || scalars.length > 0) && (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {scalars.length > 0 && (
              <div data-testid="stats-group-general" className="rounded-2xl glass-dark p-6">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-5">
                  {tx.stats.general}
                </p>
                <ul className="space-y-3">
                  {scalars.map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-400">{labelFor(k, tx.fields)}</span>
                      <span className="font-heading font-extrabold text-krug-goldbright tabular-nums">{String(v ?? '—')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {groups.map(([group, values]) => (
              <div key={group} data-testid={`stats-group-${group}`} className="rounded-2xl glass-dark p-6">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-5">
                  {labelFor(group, tx.fields)}
                </p>
                <ul className="space-y-3">
                  {Object.entries(values).map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-400">{labelFor(k, tx.fields)}</span>
                      <span className="font-heading font-extrabold text-krug-goldbright tabular-nums">
                        {v && typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminStats;
